// Safe TTS Service - Prevents stack overflow and manages audio properly
import { elevenLabsService } from './elevenlabsService';

interface TTSRequest {
  id: string;
  text: string;
  priority: 'high' | 'medium' | 'low';
  timestamp: number;
  retryCount: number;
}

interface TTSResult {
  success: boolean;
  audioUrl?: string;
  error?: string;
}

class SafeTTSService {
  private requestQueue: TTSRequest[] = [];
  private isProcessing = false;
  private currentRequest: TTSRequest | null = null;
  private maxRetries = 2;
  private maxQueueSize = 5;
  private processingTimeout: NodeJS.Timeout | null = null;
  private activeAudioElements: Set<HTMLAudioElement> = new Set();

  constructor() {
    // Clean up audio elements when they finish
    this.setupAudioCleanup();
  }

  private setupAudioCleanup(): void {
    // Periodically clean up finished audio elements
    setInterval(() => {
      this.activeAudioElements.forEach(audio => {
        if (audio.ended || audio.error) {
          this.activeAudioElements.delete(audio);
        }
      });
    }, 10000); // Clean up every 10 seconds
  }

  /**
   * Safely convert text to speech without causing stack overflow
   */
  public async textToSpeech(
    text: string, 
    priority: 'high' | 'medium' | 'low' = 'medium'
  ): Promise<TTSResult> {
    // Validate input
    if (!text || text.trim().length === 0) {
      return { success: false, error: 'Empty text provided' };
    }

    if (text.length > 5000) {
      console.warn('[SafeTTS] Text too long, truncating');
      text = text.substring(0, 5000) + '...';
    }

    const requestId = `tts_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const request: TTSRequest = {
      id: requestId,
      text: text.trim(),
      priority,
      timestamp: Date.now(),
      retryCount: 0
    };

    console.log(`[SafeTTS] Queuing TTS request: ${requestId}`);

    // Add to queue
    this.addToQueue(request);

    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }

    // Return a promise that resolves when this specific request is processed
    return new Promise((resolve) => {
      const checkCompletion = () => {
        // Check if our request is still in queue or being processed
        const stillInQueue = this.requestQueue.some(r => r.id === requestId);
        const isCurrentRequest = this.currentRequest?.id === requestId;
        
        if (!stillInQueue && !isCurrentRequest) {
          // Request completed, resolve with success
          resolve({ success: true });
        } else {
          // Check again in 100ms
          setTimeout(checkCompletion, 100);
        }
      };
      
      // Start checking after a short delay
      setTimeout(checkCompletion, 100);
    });
  }

  private addToQueue(request: TTSRequest): void {
    // Remove old requests if queue is full
    if (this.requestQueue.length >= this.maxQueueSize) {
      console.warn('[SafeTTS] Queue full, removing oldest low priority requests');
      this.requestQueue = this.requestQueue.filter(r => r.priority === 'high').slice(-2);
    }

    // Insert based on priority
    if (request.priority === 'high') {
      this.requestQueue.unshift(request);
    } else {
      this.requestQueue.push(request);
    }

    console.log(`[SafeTTS] Queue size: ${this.requestQueue.length}`);
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing) {
      console.log('[SafeTTS] Already processing queue');
      return;
    }

    this.isProcessing = true;
    console.log('[SafeTTS] Starting queue processing');

    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      if (!request) continue;

      this.currentRequest = request;
      
      try {
        console.log(`[SafeTTS] Processing request: ${request.id}`);
        
        // Set a timeout to prevent hanging
        const timeoutPromise = new Promise<never>((_, reject) => {
          this.processingTimeout = setTimeout(() => {
            reject(new Error('TTS request timeout'));
          }, 15000); // 15 second timeout
        });

        // Process the TTS request with timeout
        const ttsPromise = this.processTTSRequest(request);
        
        await Promise.race([ttsPromise, timeoutPromise]);
        
        // Clear timeout if successful
        if (this.processingTimeout) {
          clearTimeout(this.processingTimeout);
          this.processingTimeout = null;
        }

        console.log(`[SafeTTS] Successfully processed: ${request.id}`);
        
      } catch (error) {
        console.error(`[SafeTTS] Failed to process request ${request.id}:`, error);
        
        // Clear timeout
        if (this.processingTimeout) {
          clearTimeout(this.processingTimeout);
          this.processingTimeout = null;
        }
        
        // Retry if under limit
        if (request.retryCount < this.maxRetries) {
          request.retryCount++;
          console.log(`[SafeTTS] Retrying request ${request.id} (attempt ${request.retryCount})`);
          this.requestQueue.unshift(request); // Add back to front for retry
        } else {
          console.error(`[SafeTTS] Max retries exceeded for request ${request.id}`);
        }
      }
      
      this.currentRequest = null;
      
      // Small delay between requests to prevent overwhelming the API
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    this.isProcessing = false;
    console.log('[SafeTTS] Queue processing completed');
  }

  private async processTTSRequest(request: TTSRequest): Promise<void> {
    try {
      // Use the existing ElevenLabs service but with safety checks
      const audioBuffer = await elevenLabsService.textToSpeech(request.text);
      
      if (!audioBuffer || audioBuffer.byteLength === 0) {
        throw new Error('Empty audio buffer received');
      }

      // Convert to base64 for data URL (chunk to avoid stack overflow)
      const uint8Array = new Uint8Array(audioBuffer);
      const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
      let binaryString = '';

      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, i + chunkSize);
        binaryString += String.fromCharCode(...chunk);
      }

      const base64Audio = btoa(binaryString);
      const audioUrl = `data:audio/mpeg;base64,${base64Audio}`;
      
      // Validate the audio URL
      if (!this.isValidAudioUrl(audioUrl)) {
        throw new Error('Invalid audio URL generated');
      }

      console.log(`[SafeTTS] Generated audio for request ${request.id}`);
      
      // Store the result (you might want to emit an event here or store in a map)
      // For now, we'll just log success
      
    } catch (error) {
      console.error(`[SafeTTS] TTS processing error for ${request.id}:`, error);
      throw error;
    }
  }

  private isValidAudioUrl(url: string): boolean {
    try {
      // Basic validation
      if (!url.startsWith('data:audio/')) {
        return false;
      }
      
      // Check if base64 part exists and is reasonable length
      const base64Part = url.split(',')[1];
      if (!base64Part || base64Part.length < 100) {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Play audio safely without causing memory leaks
   */
  public async playAudio(audioUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const audio = new Audio(audioUrl);
        
        // Add to active elements for cleanup
        this.activeAudioElements.add(audio);
        
        audio.onended = () => {
          this.activeAudioElements.delete(audio);
          resolve();
        };
        
        audio.onerror = (error) => {
          this.activeAudioElements.delete(audio);
          reject(new Error(`Audio playback failed: ${error}`));
        };
        
        // Set volume and play
        audio.volume = 0.7;
        audio.play().catch(error => {
          this.activeAudioElements.delete(audio);
          reject(error);
        });
        
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Stop all currently playing audio
   */
  public stopAllAudio(): void {
    console.log('[SafeTTS] Stopping all audio');
    
    this.activeAudioElements.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (error) {
        console.warn('[SafeTTS] Error stopping audio:', error);
      }
    });
    
    this.activeAudioElements.clear();
  }

  /**
   * Clear the TTS queue
   */
  public clearQueue(): void {
    console.log('[SafeTTS] Clearing TTS queue');
    this.requestQueue = [];
    
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }
  }

  /**
   * Get queue status
   */
  public getQueueStatus(): {
    queueLength: number;
    isProcessing: boolean;
    currentRequest: string | null;
  } {
    return {
      queueLength: this.requestQueue.length,
      isProcessing: this.isProcessing,
      currentRequest: this.currentRequest?.id || null
    };
  }

  /**
   * Emergency stop - clears everything
   */
  public emergencyStop(): void {
    console.log('[SafeTTS] Emergency stop activated');
    
    this.clearQueue();
    this.stopAllAudio();
    this.isProcessing = false;
    this.currentRequest = null;
    
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }
  }
}

// Export singleton instance
export const safeTTSService = new SafeTTSService();